import {gql} from "@apollo/client";

import {GenericMutationResponse, GraphQLResponse} from "@/lib/types/common.ts";
import {FGM_METRICS_EXPLORER_RESULT_FIELDS} from "@/lib/types/fragments.metricsExplorer.ts";
import {
  MetricDimensionValues,
  MetricsExplorerFile,
  MetricsExplorerQueryInput,
  MetricsExplorerResult,
  MetricsExplorerSchema,
  MetricsExplorerView,
} from "@/lib/types/metricsExplorer.ts";

export interface MetricsExplorerQueryData {
  metricsExplorerQuery: MetricsExplorerResult;
}

export interface MetricsExplorerQueryVariables {
  query: MetricsExplorerQueryInput;
}

export interface MetricsExplorerSchemaData {
  metricsExplorerSchema: MetricsExplorerSchema;
}

export interface MetricsExplorerDimensionValuesData {
  metricsExplorerDimensionValues: MetricDimensionValues;
}

export const GQL_METRICS_EXPLORER_SCHEMA = gql`
  query MetricsExplorerSchema($check_type: String, $check_id: Int) {
    metricsExplorerSchema(check_type: $check_type, check_id: $check_id) {
      version
      check_types {
        check_type
        data_types {
          name
          dimensions {
            name
            type
            label
          }
          metrics {
            name
            type
            label
            unit
            numeric
          }
        }
      }
    }
  }
`;

export const GQL_METRICS_EXPLORER_DIMENSION_VALUES = gql`
  query MetricsExplorerDimensionValues(
    $check_ids: [Int!]!
    $data_type: String!
    $dimension: String!
    $start_date: DateTime
    $end_date: DateTime
  ) {
    metricsExplorerDimensionValues(
      check_ids: $check_ids
      data_type: $data_type
      dimension: $dimension
      start_date: $start_date
      end_date: $end_date
    ) {
      dimension
      values
    }
  }
`;

export const GQL_METRICS_EXPLORER_QUERY = gql`
  query MetricsExplorerQuery($query: MetricsExplorerQueryInput!) {
    metricsExplorerQuery(query: $query) {
      ...MetricsExplorerResultFields
    }
  }
  ${FGM_METRICS_EXPLORER_RESULT_FIELDS}
`;

export interface MetricsExplorerDowntimeData {
  stats: {
    check_state_events: {
      start: string;
      end: string | null;
      status: "UP" | "DOWN" | "NO_DATA";
    }[];
  };
}

/**
 * Down windows for one check, to shade behind a chart.
 *
 * Reuses getCheckStats rather than adding an endpoint: check_state_events already returns
 * exactly the windows a plotband needs, clipped to the requested range.
 */
export const GQL_METRICS_EXPLORER_DOWNTIME = gql`
  query MetricsExplorerDowntime($id: Int!, $start: DateTime!, $end: DateTime!) {
    stats: getCheckStats(input: {id: $id, start: $start, end: $end}) {
      check_state_events {
        start
        end
        status
      }
    }
  }
`;

export interface MetricsExplorerViewsData {
  metricsExplorerViews: {
    total: number;
    items: MetricsExplorerView[];
  };
}

export type SaveMetricsExplorerViewResult = GraphQLResponse<MetricsExplorerView>;
export type DeleteMetricsExplorerViewResult = GenericMutationResponse;

// Field lists are written out rather than interpolated so the documents stay statically
// checkable against the schema.
export const GQL_METRICS_EXPLORER_VIEWS = gql`
  query MetricsExplorerViews {
    metricsExplorerViews {
      total
      items {
        id
        name
        description
        config
        is_pinned
        created_by_name
        created_at
        modified_at
      }
    }
  }
`;

export const GQL_METRICS_EXPLORER_VIEW_SAVE = gql`
  mutation MetricsExplorerViewSave($id: Int, $data: MetricsExplorerViewInput!) {
    response: metricsExplorerViewSave(id: $id, data: $data) {
      ... on SaveMetricsExplorerViewSuccess {
        status
        data {
          id
          name
          description
          config
          is_pinned
          created_by_name
          created_at
          modified_at
        }
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_METRICS_EXPLORER_VIEW_DELETE = gql`
  mutation MetricsExplorerViewDelete($id: Int!) {
    response: metricsExplorerViewDelete(id: $id) {
      ... on GenericMutationResponseSuccess {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export interface MetricsExplorerExportData {
  metricsExplorerExport: MetricsExplorerFile;
}

export const GQL_METRICS_EXPLORER_EXPORT = gql`
  query MetricsExplorerExport($input: MetricsExplorerExportInput!) {
    metricsExplorerExport(input: $input) {
      filename
      content_type
      data
      truncated
    }
  }
`;
